import {
  BackgroundColor,
  MosaicLayout,
  MosaicLayoutVariantType,
  cardImageRatioForMosaicIndex
} from "@silversea-ssc/ui-components-react";
import React, { useCallback } from "react";
import {
  PageBuilderCardMosaicFragment,
  PageBuilderCardMosaicModalItemFragment
} from "types/graphql-types";
import { CardColor } from "../pageBuilderRenderHelper";
import { CardAction } from "components/Card/cardAction";
import LinkCard, { LinkCardProps } from "components/Card/LinkCard";
import MultiLine from "components/Text/MultiLine";
import { cardCTAButtonToCardAction } from "./helpers";
const ImmersivePictureCard = React.lazy(
  () => import("components/Card/ImmersivePictureCard/ImmersivePictureCard")
);

export type CardVariant = "default" | "immersive";

type Props = {
  mosaicColumnVariant: MosaicLayoutVariantType;
  items: PageBuilderCardMosaicFragment["items"];
  cardBackgroundColor: CardColor;
  mosaicCardVariant: CardVariant;
  onShowModal: React.Dispatch<
    React.SetStateAction<PageBuilderCardMosaicModalItemFragment | undefined>
  >;
  onClickAnchor: (target: string | undefined) => void;
};

const PageBuilderCardMosaicStandard: React.FC<Props> = ({
  items = [],
  mosaicColumnVariant,
  cardBackgroundColor,
  mosaicCardVariant,
  onShowModal,
  onClickAnchor
}) => {
  return (
    <MosaicLayout variant={mosaicColumnVariant} data-content-name="card_mosaic">
      {items.map((item, index) => {
        if (!item) return null;
        const cardRatio = cardImageRatioForMosaicIndex(
          mosaicColumnVariant,
          index
        );
        return (
          <PageBuilderCardMosaicInternalCard
            key={item._key}
            item={item}
            cardBackgroundColor={cardBackgroundColor}
            imageRatio={cardRatio}
            variant={mosaicCardVariant}
            onClickAnchor={onClickAnchor}
            onShowModal={onShowModal}
          />
        );
      })}
    </MosaicLayout>
  );
};

export default PageBuilderCardMosaicStandard;

type PageBuilderCardMosaicInternalCard = {
  cardBackgroundColor?: BackgroundColor;
  imageRatio?: LinkCardProps["imageRatio"];
  variant: CardVariant;
  item: NonNullable<NonNullable<PageBuilderCardMosaicFragment["items"]>[0]>;
  onShowModal: React.Dispatch<
    React.SetStateAction<PageBuilderCardMosaicModalItemFragment | undefined>
  >;
  onClickAnchor: (section: string | undefined) => void;
};

const PageBuilderCardMosaicInternalCard: React.FC<
  PageBuilderCardMosaicInternalCard
> = ({
  item,
  imageRatio,
  variant,
  onShowModal,
  onClickAnchor,
  cardBackgroundColor
}) => {
  const onInternalShowModalClick = useCallback(() => {
    if (item.__typename == "SanityMosaicCardModal") {
      onShowModal(item);
    }
  }, [onShowModal, item]);

  const action =
    item.__typename == "SanityMosaicCardModal"
      ? { onClick: onInternalShowModalClick, variant: undefined }
      : (cardCTAButtonToCardAction(onClickAnchor)(
          item.ctaButtons?.[0]
        ) satisfies CardAction | undefined);

  if (variant === "immersive") {
    <React.Suspense fallback={null}>
      <ImmersivePictureCard
        altText={item.title?.localized}
        image={item.image}
        title={item.title?.localized}
        trackingName="more_image"
        action={action}
      />
    </React.Suspense>;
  }

  return (
    <LinkCard
      backgroundColor={cardBackgroundColor}
      image={item.image}
      imageRatio={imageRatio}
      title={item.title?.localized}
      description={<MultiLine>{item.description?.localized}</MultiLine>}
      action={action}
    />
  );
};
